import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download } from 'lucide-react';
import { exportMultipleToMarkdown, exportMultipleToJson, exportMultipleToCsv } from '@/lib/export-utils';
import { Recipe } from '@/types/recipe';
import { Card, CardContent } from '@/components/ui/card';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecipes: Recipe[];
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, selectedRecipes }) => {
  const [exportFormat, setExportFormat] = useState<string>('markdown');

  const handleExport = async () => {
    try {
      if (exportFormat === 'markdown') {
        await exportMultipleToMarkdown(selectedRecipes);
      } else if (exportFormat === 'json') {
        await exportMultipleToJson(selectedRecipes);
      } else if (exportFormat === 'csv') {
        await exportMultipleToCsv(selectedRecipes);
      }
      onClose();
    } catch {
    }
  };

  const formatOptions = [
    { id: 'markdown', label: 'Markdown', description: 'Export as individual Markdown files, ideal for readability.' },
    { id: 'json', label: 'JSON', description: 'Export as a single JSON file, perfect for data backup.' },
    { id: 'csv', label: 'CSV', description: 'Export as a CSV file, suitable for spreadsheet analysis.' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md md:max-w-lg p-6 rounded-lg shadow-lg bg-white dark:bg-gray-800">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white">Export Recipes</DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-300">
            Export {selectedRecipes.length} recipe(s) in your preferred format.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {formatOptions.map((option) => (
            <Card
              key={option.id}
              className={`cursor-pointer border-2 ${
                exportFormat === option.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
              onClick={() => setExportFormat(option.id)}
            >
              <CardContent className="p-4 flex flex-col items-center text-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    exportFormat === option.id ? 'bg-blue-100 dark:bg-blue-800' : 'bg-gray-100 dark:bg-gray-800'
                  }`}
                >
                  <Download
                    className={`h-5 w-5 ${
                      exportFormat === option.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                    }`}
                  />
                </div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">{option.label}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{option.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <DialogFooter className="mt-6 flex justify-end gap-3">
          <DialogClose asChild>
            <Button variant="outline" className="rounded-md">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleExport} className="rounded-md bg-blue-600 hover:bg-blue-700 text-white">
            Export {selectedRecipes.length} Recipe(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
